import { useState, useEffect } from 'react';
import { authService, supabase } from '../services/auth.service';
import { chatService } from '../services/api';
import config from '../config';

export const useAuth = () => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    // Initial session check
    checkSession();

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(handleAuthChange);

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const checkSession = async () => {
    try {
      const currentSession = await authService.getSession();
      setSession(currentSession);
      if (currentSession) {
        await validateAndSetUser(currentSession);
      }
    } catch (error) {
      console.error('Error checking session:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAuthChange = async (event, session) => {
    setSession(session);
    if (session) {
      await validateAndSetUser(session);
    } else {
      setUserData(null);
      localStorage.removeItem('access_token');
    }
  };

  const validateAndSetUser = async (session) => {
    try {
      const response = await fetch(`${config.api.baseUrl}/auth/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        localStorage.setItem('access_token', session.access_token);
        const { status, user_data } = await chatService.userInfo();
        if (status === "success" && user_data) {
          setUserData(user_data);
        } else {
          await signOut();
        }
      } else {
        await signOut();
      }
    } catch (error) {
      console.error('Failed to validate user:', error);
      await signOut();
    }
  };

  const signOut = async () => {
    try {
      await authService.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      // Always clean up local state and cookies, regardless of Supabase signout success
      setSession(null);
      setUserData(null);
      localStorage.removeItem('access_token');
      document.cookie = 'access_token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=Lax;';
    }
  };

  return {
    session,
    loading,
    signOut,
    isAuthenticated: !!session,
    userData,
    data: { subscription: null } 
  };
};

export default useAuth;
